const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createCallPeerLeftEvent = () => new CustomEvent('call:peer-left');
const createCallAudioTracksEvent = () => new CustomEvent('call:audio-tracks');

// ~500KB gzipped — kept out of the dashboard's initial bundle so agents who
// never take a web call don't pay for it.
let AgoraRTC = null;
const loadSdk = async () => {
  AgoraRTC ||= (await import('agora-rtc-sdk-ng')).default;
  return AgoraRTC;
};

// Singleton Agora client for dashboard web calls — mirrors RealtimeKitCallClient's
// shape (EventTarget singleton, `call:disconnected` / `call:peer-left` events) so
// useCallSession's global listener wiring works unchanged across engines.
class AgoraCallClient extends EventTarget {
  constructor() {
    super();
    this.client = null;
    this.microphoneTrack = null;
    this.muted = false;
  }

  async connect({ app_id: appId, channel, token, uid }) {
    if (this.client) await this.disconnect();

    const sdk = await loadSdk();
    const client = sdk.createClient({ mode: 'rtc', codec: 'vp8' });
    this.client = client;

    client.on('user-published', async (user, mediaType) => {
      // Guard against a stale client still firing events after a newer
      // connect() (or a disconnect()) has already replaced/cleared it.
      if (this.client !== client || mediaType !== 'audio') return;
      await client.subscribe(user, mediaType);
      user.audioTrack?.play();
      // The call recorder (see webCallClient) starts on the customer's audio.
      this.dispatchEvent(createCallAudioTracksEvent());
    });
    // The customer has no cable connection to tell us they hung up, so their
    // leaving the channel is the agent-side signal that the call is over —
    // mirroring the RealtimeKit client's participantLeft handling.
    client.on('user-left', () => {
      if (this.client !== client) return;
      if (client.remoteUsers.length > 0) return;
      this.dispatchEvent(createCallPeerLeftEvent());
    });
    // Losing the channel without us asking (network drop, token rejected)
    // is the Agora equivalent of RealtimeKit's roomLeft. disconnect() clears
    // `this.client` before leaving, so a deliberate teardown fails the guard
    // and doesn't double-report.
    client.on('connection-state-change', currentState => {
      if (this.client !== client || currentState !== 'DISCONNECTED') return;
      this.client = null;
      this.microphoneTrack?.close();
      this.microphoneTrack = null;
      this.dispatchEvent(createCallDisconnectedEvent());
    });

    await client.join(appId, channel, token, uid);
    this.microphoneTrack = await sdk.createMicrophoneAudioTrack();
    await client.publish([this.microphoneTrack]);
    // Guard against a stale client still resolving after a newer connect()
    // (or a disconnect()) has already replaced/cleared it.
    if (this.client !== client) return;
    // An inbound customer published before we joined: their track is already
    // here, and ours exists only now.
    this.dispatchEvent(createCallAudioTracksEvent());
  }

  async disconnect() {
    if (!this.client) return;
    const client = this.client;
    this.client = null;
    // Closing the track releases the mic; leaving without it keeps the
    // browser's recording indicator lit after the call has ended.
    this.microphoneTrack?.close();
    this.microphoneTrack = null;
    await client.leave();
  }

  // setMuted keeps the track published, so the customer sees a muted agent
  // rather than one whose audio vanished.
  async setMuted(muted) {
    this.muted = muted;
    if (!this.microphoneTrack) return;
    await this.microphoneTrack.setMuted(muted);
  }

  // Agent-side hold: stop the customer's audio locally and stop publishing.
  // An Agora channel has no server-side hold, so this IS the hold (design 8.2).
  async setHold(held) {
    if (!this.client) return;
    this.client.remoteUsers.forEach(user => {
      if (held) user.audioTrack?.stop();
      else user.audioTrack?.play();
    });
    await this.microphoneTrack?.setMuted(held || this.muted);
  }

  // Every party's audio for the call recorder — a conference transfer can put
  // two agents plus the customer in one channel, so every remote user with an
  // audio track counts, not just the first.
  getAudioTracks() {
    const remotes = this.client
      ? this.client.remoteUsers.filter(user => user.audioTrack)
      : [];
    return {
      localTrack: this.microphoneTrack?.getMediaStreamTrack() || null,
      remoteTracks: remotes.map(user => user.audioTrack.getMediaStreamTrack()),
    };
  }

  get isConnected() {
    return Boolean(this.client);
  }
}

export default new AgoraCallClient();
